import * as wizeMobileApi from '@wize-mobile-api';
import {
  Account,
  Amount,
  Country,
  Currency,
  Payment,
  PaymentCategory,
  PaymentFee,
  PaymentFeeIdEnum,
  PaymentStatus,
  PaymentStatusEnum,
  Portfolio,
  SwiftCode,
  Transfer
} from '@qvs/core';
import { IbanBicSwiftService } from '~/lazy/payments/services/iban-bic-swift.service';
import { NewPayment } from '~/lazy/payments/pages/new-payment/models/newPayment';
import {
  OrdersPaymentRequest,
  OrdersPaymentRequestStatusEnum
} from '~/lazy/payments/pages/new-payment/models/ordersPaymentRequest';
import { PaymentScannerResult } from '~/lazy/payments/models/PaymentScannerResult';
import { EsrParser } from '~/lazy/payments/services/esr.parser';
import { PaymentTemplate } from '~/lazy/payments/pages/new-payment/models/paymentTemplate';
import { NewTransfer } from '~/lazy/payments/pages/new-transfer/models/NewTransfer';
import { isValidBIC, isValidIBAN } from '~/lazy/payments/services/lib/IBANTools';
import { paymentFees } from '~/lazy/payments/models/payment.fees';
import { DateUtils } from '~/shared/utils/date.utils';
import { isNil } from 'lodash-es';

export class PaymentMapper {
  public static fromWizePayments(payments: wizeMobileApi.PaymentMobile[]): Payment[] {
    if (payments) {
      return payments.map((payment) => {
        return PaymentMapper.fromWizePayment(payment);
      });
    }
    return undefined;
  }

  public static fromWizePayment(payment: wizeMobileApi.PaymentMobile) {
    const mappedPayment = {
      id: payment.id,
      generalOrderId: payment.generalOrderId,
      accountId: payment.accountId,
      ibanIdentification: payment.ibanIdentification,
      formattedIbanIdentification: payment.formattedIbanIdentification,
      portfolioId: payment.portfolioId,
      orderDate: DateUtils.new(payment.orderDate).toDate(),
      orderDateYearMonth: DateUtils.format('YYYYMM', payment.orderDate),
      operationDate: DateUtils.new(payment.operationDate).toDate(),
      deferredExecutionDate: payment.defferedExecutionDate ? DateUtils.new(payment.defferedExecutionDate).toDate() : null,
      valueDate: DateUtils.new(payment.valueDate).toDate(),
      accountingDate: DateUtils.new(payment.accountingDate).toDate(),
      debitedGrossAmount: payment.debitedGrossAmount,
      debitedNetAmount: PaymentMapper.mapAmount(payment.debitedNetAmount, payment.debitedCurrencyId),
      debitedCurrencyId: payment.debitedCurrencyId,
      beneficiaryCurrencyId: payment.beneficiaryCurrencyId,
      beneficiaryAmount: PaymentMapper.mapAmount(payment.beneficiaryAmount, payment.beneficiaryCurrencyId),
      beneficiaryAccountTypeValue: payment.beneficiaryAccountTypeValue,
      beneficiaryAccount: payment.beneficiaryAccount,
      beneficiaryName: payment.beneficiaryName,
      beneficiaryStreet: payment.beneficiaryStreet,
      beneficiaryStreetNumber: payment.beneficiaryStreetNumber,
      beneficiaryPostalCode: payment.beneficiaryPostalCode,
      beneficiaryCity: payment.beneficiaryTown,
      beneficiaryCommunications: payment.beneficiaryCommunications,
      beneficiaryPaymentRef: payment.beneficiaryCommunications,
      beneficiaryCountryId: payment.beneficiaryCountryId,
      status: PaymentMapper.fromWizePaymentStatus(payment),
      beneficiaryAccountFriendlyFormatted: IbanBicSwiftService.friendlyFormatIbanOrAccount(payment.beneficiaryAccount),
      beneficiaryBicSwift: payment.beneficiaryBic,
      beneficiaryABARoutingNumber: payment.beneficiaryABARoutingNumber,
      beneficiaryBankName: payment.beneficiaryBankName,
      analyticalCodeTypeId: payment.analyticalCodeTypeId,
      bookingText: payment.debitMovementDescription,
      chargesRuleValue: payment.chargesRuleValue
    };

    Object.keys(mappedPayment).forEach((key) => {
      if (mappedPayment[key] === 'undefined') {
        mappedPayment[key] = undefined;
      }
    });

    return mappedPayment;
  }

  public static fromWizePaymentStatus(
    payment: wizeMobileApi.PaymentMobile | wizeMobileApi.InternalTransferMobile
  ): PaymentStatus {
    let status = PaymentStatusEnum.PENDING;

    if (payment) {
      if (payment.apiOrderStatus) {
        status = this.mapApiOrderStatusToPaymentStatus(payment.apiOrderStatus);
      }

      if (
        status !== PaymentStatusEnum.APPROVED &&
        payment.apiOrderCreationStatus &&
        payment.apiOrderCreationStatus !== wizeMobileApi.PaymentMobile.ApiOrderCreationStatusEnum.AUTHORIZATION_SUCCEEDED
      ) {
        if (
          (
            [
              wizeMobileApi.PaymentMobile.ApiOrderCreationStatusEnum.AUTHORIZATION_FAILED,
              wizeMobileApi.PaymentMobile.ApiOrderCreationStatusEnum.AUTHORIZATION_EXPIRED
            ] as wizeMobileApi.PaymentMobile.ApiOrderCreationStatusEnum[]
          ).includes(payment.apiOrderCreationStatus)
        ) {
          return status === PaymentStatusEnum.DECLINED ? status : PaymentStatusEnum.UNREGISTERED_TIMED_OUT;
        }
        return PaymentStatusEnum.UNREGISTERED;
      }
    }
    return status;
  }

  public static fromWizeTransfers(transfers: wizeMobileApi.InternalTransferMobile[], portfolios: Portfolio[]): Transfer[] {
    return transfers
      ? transfers.map((transfer) => ({
          id: transfer.id,
          accountId: transfer.accountId,
          orderingAmount: {
            value: transfer.orderingAmount ? Math.abs(transfer.orderingAmount) : 0,
            currency: transfer.orderingCurrencyId
          },
          creditedAmount: {
            value: transfer.creditedAmount,
            currency: transfer.creditedCurrencyId
          },
          creditedAccountId: transfer.creditedAccountId,
          portfolioId: transfer.portfolioId,
          portfolioName: portfolios.find((portfolio) => portfolio.portfolioNumber === transfer.portfolioId)?.name,
          orderDate: DateUtils.new(transfer.orderDate).toDate(),
          orderDateYearMonth: DateUtils.format('YYYYMM', transfer.orderDate),
          deferredExecutionDate: transfer.defferedExecutionDate ? DateUtils.new(transfer.defferedExecutionDate).toDate() : null,
          analyticalCodeTypeId: transfer.orderingAnalyticalCodeTypeId || transfer.beneficiaryAnalyticalCodeTypeId,
          debitMovementDescription: transfer.debitMovementDescription,
          creditMovementDescription: transfer.creditMovementDescription,
          status: PaymentMapper.fromWizePaymentStatus(transfer)
        }))
      : undefined;
  }

  public static fromWizeCategories(categories: wizeMobileApi.AnalyticalCodeType[]): PaymentCategory[] {
    return categories
      ? categories.map((category) => ({
          id: category.id,
          label: category.description,
          priority: categories.length + 2,
          ranking: category.ranking
        }))
      : undefined;
  }

  public static fromWizeSwiftCodes(
    swiftCodes: wizeMobileApi.SwiftBicCode[],
    existingSwiftCodes: SwiftCode[],
    countries: Country[]
  ): SwiftCode[] {
    if (!!swiftCodes || !!existingSwiftCodes || !!countries) {
      const swiftCodesResult = [...existingSwiftCodes];
      const countryIdMap = {};
      const swiftCodesMap = {};
      existingSwiftCodes.forEach((code) => (swiftCodesMap[code.code] = true));
      swiftCodes.forEach((code) => {
        if (!swiftCodesMap[code.id]) {
          if (countryIdMap[code.countryId] === undefined) {
            const mappedCountry = countries.find((country) => country.id === code.countryId);
            countryIdMap[code.countryId] = mappedCountry ? mappedCountry.label : null;
          }
          swiftCodesResult.push({
            code: code.id,
            country: countryIdMap[code.countryId],
            countryId: code.countryId,
            city: code.cityHeading,
            bank: code.institutionName,
            branchName: code.branch,
            branchAddress: code.branchInformation,
            branchCode: null
          });
        }
      });

      return swiftCodesResult;
    }
    return undefined;
  }

  public static toWizePaymentRequest(
    request: OrdersPaymentRequest,
    categories: PaymentCategory[]
  ): wizeMobileApi.PaymentMobileInputFormRequest {
    if (!request?.payment) {
      return {} as any;
    }
    return {
      defferedExecutionDate: request.payment.executionDate ? DateUtils.format('YYYY-MM-DD', request.payment.executionDate) : null,
      orderDate: DateUtils.format('YYYY-MM-DDTHH:mm:ss[Z]'),
      beneficiaryStreet: request.payment.beneficiaryStreet,
      beneficiaryStreetNumber: request.payment.beneficiaryStreetNumber,
      beneficiaryPostalCode: request.payment.beneficiaryPostalCode,
      beneficiaryTown: request.payment.beneficiaryCity,
      beneficiaryName: request.payment.beneficiaryName,
      beneficiaryCountryId: request.payment.beneficiaryCountryId,
      beneficiaryCommunications: `${request.payment.beneficiaryPaymentRef || ''}\n${
        request.payment.beneficiaryPaymentRefAdditionalTextElement || ''
      }`.trim(),
      beneficiaryAccount: request.payment.beneficiaryIbanOrAccount,
      beneficiaryAccountTypeValue: PaymentMapper.toWizeBeneficiaryAccountTypeValue(request.payment),
      beneficiaryAmount: request.payment.amount,
      beneficiaryCurrencyId: request.payment.currency?.currency,
      analyticalCodeTypeId: (request.payment.bookingCategory || categories.find((cat) => cat.id === '180'))?.id || '180',
      accountId: request.payment.account?.id,
      chargesRuleValue: PaymentMapper.toWizeChargesRuleValue(request.payment.fees),
      beneficiarySwiftBicCodeId:
        request.payment.beneficiaryBicSwift !== request.payment.beneficiaryABARoutingNumber
          ? request.payment.beneficiaryBicSwift
          : '',
      beneficiaryABARoutingNumber: request.payment.beneficiaryABARoutingNumber,
      beneficiaryBankName: request.payment.beneficiaryBankName,
      debitMovementDescription: request.payment.bookingText
    };
  }

  public static toWizeInternalTransferRequest(
    request: OrdersPaymentRequest,
    categories: PaymentCategory[]
  ): wizeMobileApi.InternalTransferMobileInputRequest {
    if (!request?.transfer) {
      return {} as any;
    }
    return {
      beneficiaryCountryId: PaymentMapper.toWizeBeneficiaryCountryId(request.transfer),
      orderingPartyCountryId: PaymentMapper.toWizeBeneficiaryCountryId(request.transfer),
      accountId: request.transfer.debitAccount.id,
      debitMovementDescription: request.transfer.communicationToDebitedAccount,
      creditedAccountId: request.transfer.creditAccount.id,
      creditMovementDescription: request.transfer.communicationToCreditedAccount,
      orderingAmount: request.transfer.amount,
      defferedExecutionDate: request.transfer.executionDate
        ? DateUtils.format('YYYY-MM-DD', request.transfer.executionDate)
        : null,
      orderDate: DateUtils.format('YYYY-MM-DDTHH:mm:ss[Z]'),
      beneficiaryAnalyticalCodeTypeId:
        (request.transfer.bookingCategory || categories.find((cat) => cat.id === '180'))?.id || null,
      orderingAnalyticalCodeTypeId: (request.transfer.bookingCategory || categories.find((cat) => cat.id === '180'))?.id || null
    };
  }

  public static toWizeChargesRuleValue(fee: PaymentFee): wizeMobileApi.PaymentMobileInputFormRequest.ChargesRuleValueEnum {
    if (fee?.id === 'BENEFICIARY') {
      return wizeMobileApi.PaymentMobileInputFormRequest.ChargesRuleValueEnum.TO_BENEFICIARY;
    }
    if (fee?.id === 'SENDER') {
      return wizeMobileApi.PaymentMobileInputFormRequest.ChargesRuleValueEnum.TO_ORDERING_PARTY;
    }
    if (fee?.id === 'SHARED') {
      return wizeMobileApi.PaymentMobileInputFormRequest.ChargesRuleValueEnum.SHARED;
    }
    return wizeMobileApi.PaymentMobileInputFormRequest.ChargesRuleValueEnum.SHARED;
  }

  public static fromWizeChargesRuleValue(
    chargesRuleValue: wizeMobileApi.PaymentMobileInputFormRequest.ChargesRuleValueEnum
  ): PaymentFee {
    switch (chargesRuleValue) {
      case wizeMobileApi.PaymentMobileInputFormRequest.ChargesRuleValueEnum.TO_ORDERING_PARTY:
        return paymentFees.find((fee) => fee.id === PaymentFeeIdEnum.SENDER);
      case wizeMobileApi.PaymentMobileInputFormRequest.ChargesRuleValueEnum.TO_BENEFICIARY:
        return paymentFees.find((fee) => fee.id === PaymentFeeIdEnum.BENEFICIARY);
      case wizeMobileApi.PaymentMobileInputFormRequest.ChargesRuleValueEnum.SHARED:
      default:
        return paymentFees.find((fee) => fee.id === PaymentFeeIdEnum.SHARED);
    }
  }

  public static toWizeBeneficiaryAccountTypeValue(
    payment: NewPayment
  ): wizeMobileApi.PaymentMobile.BeneficiaryAccountTypeValueEnum {
    if (IbanBicSwiftService.isValidIban(payment?.beneficiaryIbanOrAccount)) {
      return wizeMobileApi.PaymentMobile.BeneficiaryAccountTypeValueEnum.IBAN_ACCOUNT;
    }
    return wizeMobileApi.PaymentMobile.BeneficiaryAccountTypeValueEnum.BANK_ACCOUNT;
  }

  public static toWizeBeneficiaryCountryId(payment: NewPayment): string {
    if (isValidIBAN(payment?.beneficiaryIbanOrAccount)) {
      return payment.beneficiaryIbanOrAccount.trim().slice(0, 2).toUpperCase();
    }
    if (
      payment?.paymentTemplate === PaymentTemplate.swissQrWithoutReferenceNr ||
      payment?.paymentTemplate === PaymentTemplate.swissQrWithReferenceNr
    ) {
      return 'CH';
    }

    if (isValidBIC(payment?.beneficiaryBicSwift)) {
      return payment.beneficiaryBicSwift.slice(4, 6);
    }
    return 'CH';
  }

  public static fromPaymentScannerToNewPayment(
    scan: PaymentScannerResult,
    currencies: Currency[],
    existingData?: NewPayment,
    countries?: Country[]
  ): NewPayment {
    if (!!scan || !!currencies) {
      const payment: NewPayment = existingData ? { ...existingData } : {};
      if (scan.qr) {
        payment.amount = parseFloat(scan.qr.CcyAmt.Amt);
        payment.currency = currencies.find((currency) => currency.currency === scan.qr.CcyAmt.Ccy);
        payment.beneficiaryIbanOrAccount = IbanBicSwiftService.friendlyFormatIbanOrAccount(scan.qr.CdtrInf?.IBAN);
        payment.beneficiaryName = scan.qr.CdtrInf?.Cdtr.Name || '';
        payment.beneficiaryStreet = scan.qr.CdtrInf?.Cdtr?.StrNameOrAdrLine1 || '';
        payment.beneficiaryStreetNumber = scan.qr.CdtrInf?.Cdtr?.StrNameOrAdrLine2 || '';
        payment.beneficiaryPostalCode = scan.qr.CdtrInf?.Cdtr?.PstCd || '';
        payment.beneficiaryCity = scan.qr.CdtrInf?.Cdtr?.TmwNm || '';
        payment.beneficiaryCountryId = scan.qr.CdtrInf?.Cdtr?.Ctry || '';
        payment.beneficiaryPaymentRef = `${scan.qr.RmtInf?.Ref || scan.qr.RmtInf?.AddInf.Ustrd}` || '';
        payment.bookingText = `${scan.qr.CdtrInf?.Cdtr?.Name ?? ''}` || '';

        if (scan.qr.RmtInf?.Ref) {
          payment.beneficiaryPaymentRef = EsrParser.formatReferenceNumber(payment.beneficiaryPaymentRef);
          payment.paymentTemplate = PaymentTemplate.swissQrWithReferenceNr;
          payment.beneficiaryPaymentRefAdditionalTextElement =
            `${(scan.qr.RmtInf?.AddInf?.Ustrd || scan.qr.RmtInf?.AddInf?.StrdBkgInf) ?? ''}` || '';
        } else {
          payment.beneficiaryPaymentRefAdditionalTextElement = '';
          payment.paymentTemplate = PaymentTemplate.swissQrWithoutReferenceNr;
        }
      }
      return payment;
    }
    return undefined;
  }

  public static fromRecentPaymentToNewPayment(
    payment: Payment,
    currencies: Currency[],
    accounts: Account[],
    categories: PaymentCategory[]
  ): NewPayment {
    if (payment && currencies && accounts && categories) {
      return {
        beneficiaryIbanOrAccount: payment.beneficiaryAccount,
        beneficiaryPaymentRef: payment.beneficiaryPaymentRef,
        beneficiaryName: payment.beneficiaryName,
        beneficiaryStreet: payment.beneficiaryStreet,
        beneficiaryStreetNumber: payment.beneficiaryStreetNumber,
        beneficiaryPostalCode: payment.beneficiaryPostalCode,
        beneficiaryCity: payment.beneficiaryCity,
        beneficiaryCountryId: payment.beneficiaryCountryId,
        currency: currencies.find((currency) => currency.currency === payment.beneficiaryAmount?.currency),
        account: accounts.find((account) => account.id === payment.accountId),
        beneficiaryBicSwift: payment.beneficiaryBicSwift || payment.beneficiaryABARoutingNumber,
        beneficiaryABARoutingNumber: payment.beneficiaryABARoutingNumber,
        beneficiaryBankName: payment.beneficiaryBankName,
        fees: PaymentMapper.fromWizeChargesRuleValue(payment.chargesRuleValue),
        bookingCategory: categories.find((cat) => cat.id === payment.analyticalCodeTypeId),
        bookingText: payment.bookingText
      };
    }
    return undefined;
  }

  public static fromWizePaymentMobileToOrdersPaymentRequest(
    paymentMobile: wizeMobileApi.PaymentMobile,
    countries: Country[],
    existingOrderPaymentRequest?: OrdersPaymentRequest
  ): OrdersPaymentRequest {
    if (paymentMobile) {
      const request: OrdersPaymentRequest = existingOrderPaymentRequest
        ? { ...existingOrderPaymentRequest }
        : ({
            payment: {}
          } as any);
      request.payment = {
        ...PaymentMapper.fromWizePayment(paymentMobile),
        ...request.payment
      };

      if (paymentMobile.orderStatus || paymentMobile.apiOrderCreationStatus) {
        request.status = PaymentMapper.fromWizePaymentStatus(paymentMobile);
      } else {
        request.status = existingOrderPaymentRequest?.status || null;
      }

      request.waitingForAccessAppConfirmation =
        request.status === 'UNREGISTERED' &&
        paymentMobile.apiOrderCreationStatus ===
          wizeMobileApi.PaymentMobile.ApiOrderCreationStatusEnum.WAITING_AUTHORIZATION_CONFIRMATION;

      return request;
    }
    return undefined;
  }

  public static fromWizeInternalTransferMobileToOrdersPaymentRequest(
    transferMobile: wizeMobileApi.InternalTransferMobile,
    existingOrderPaymentRequest?: OrdersPaymentRequest
  ): OrdersPaymentRequest {
    if (transferMobile) {
      const request: OrdersPaymentRequest = existingOrderPaymentRequest
        ? { ...existingOrderPaymentRequest }
        : ({ transfer: {} } as any);
      const transfer = PaymentMapper.fromWizeTransfers([transferMobile], [])[0];
      request.transfer = {
        ...transfer,
        ...request.transfer
      };

      if (transferMobile.orderStatus || transferMobile.apiOrderCreationStatus) {
        request.status = PaymentMapper.fromWizePaymentStatus(transferMobile);
      } else {
        request.status = existingOrderPaymentRequest?.status || null;
      }

      request.waitingForAccessAppConfirmation =
        request.status === 'UNREGISTERED' &&
        transferMobile.apiOrderCreationStatus ===
          wizeMobileApi.PaymentMobile.ApiOrderCreationStatusEnum.WAITING_AUTHORIZATION_CONFIRMATION;

      return request;
    }
    return undefined;
  }

  public static fromRecentTransferToNewTransfer(transfer: Transfer, accounts: Account[]): NewTransfer {
    return transfer
      ? {
          bookingCategory: transfer.bookingCategory,
          debitAccount: accounts.find((account) => account.id === transfer.accountId),
          creditAccount: accounts.find((account) => account.id === transfer.creditedAccountId),
          communicationToCreditedAccount: transfer.creditMovementDescription,
          communicationToDebitedAccount: transfer.debitMovementDescription,
          amount: transfer.orderingAmount.value
        }
      : undefined;
  }

  public static fromWizeSwiftCodeToSwiftCode(swiftCode: wizeMobileApi.SwiftBicCode, countries: Country[]): SwiftCode {
    if (!swiftCode) {
      return {
        code: '',
        bank: '',
        branchName: ''
      };
    }
    return {
      code: swiftCode.id,
      branchName: swiftCode.branchInformation,
      branchAddress: `${swiftCode.physicalAddress1 || ''}\n${swiftCode.physicalAddress2 || ''}\n${
        swiftCode.physicalAddress3 || ''
      }`.replace(/\n{2,3}/g, ''),
      bank: swiftCode.institutionName,
      branchCode: swiftCode.branch,
      city: swiftCode.cityHeading,
      countryId: swiftCode.countryId,
      country: countries.find((country) => country.id === swiftCode.countryId)?.label
    };
  }

  public static isOrdersPaymentRequestRunning(request: OrdersPaymentRequest): boolean {
    return (
      request?.status !== OrdersPaymentRequestStatusEnum.readyToSubmit &&
      !PaymentMapper.isOrdersPaymentRequestSuccessfullyRegistered(request) &&
      !PaymentMapper.isOrdersPaymentRequestRegistrationFailed(request)
    );
  }

  public static isOrdersPaymentRequestSuccessfullyRegistered(request: OrdersPaymentRequest): boolean {
    if (!request?.status) {
      return false;
    }

    return (
      request.status === PaymentStatusEnum.APPROVED ||
      request.status === PaymentStatusEnum.DECLINED ||
      request.status === PaymentStatusEnum.PENDING
    );
  }

  public static isOrdersPaymentWaitingForAccessAppConfirmation(request: OrdersPaymentRequest): boolean {
    return (
      request?.status === wizeMobileApi.PaymentAuthorizationResponse.ApiOrderCreationStatusEnum.WAITING_AUTHORIZATION_CONFIRMATION
    );
  }

  public static isOrdersPaymentRequestRegistrationFailed(request: OrdersPaymentRequest): boolean {
    if (request?.status === OrdersPaymentRequestStatusEnum.userSubmitFailure) {
      return true;
    }

    if (request?.status === PaymentStatusEnum.UNREGISTERED_TIMED_OUT) {
      return true;
    }

    if (request?.status === PaymentStatusEnum.DECLINED) {
      return true;
    }

    return !request?.status;
  }

  public static isOrdersPaymentRequestRegistrationSuccessful(request: OrdersPaymentRequest): boolean {
    return [PaymentStatusEnum.APPROVED, PaymentStatusEnum.PENDING].includes(request?.status as PaymentStatusEnum);
  }
  private static mapApiOrderStatusToPaymentStatus(
    apiOrderStatus: wizeMobileApi.PaymentMobile.ApiOrderStatusEnum
  ): PaymentStatusEnum {
    switch (apiOrderStatus) {
      case wizeMobileApi.PaymentMobile.ApiOrderStatusEnum.APPROVED:
      case wizeMobileApi.PaymentMobile.ApiOrderStatusEnum.EXECUTED:
      case wizeMobileApi.PaymentMobile.ApiOrderStatusEnum.ACCOUNTED:
        return PaymentStatusEnum.APPROVED;
      case wizeMobileApi.PaymentMobile.ApiOrderStatusEnum.DECLINED:
      case wizeMobileApi.PaymentMobile.ApiOrderStatusEnum.FAILED:
        return PaymentStatusEnum.DECLINED;
      default:
        return PaymentStatusEnum.PENDING;
    }
  }

  private static mapAmount(value?: number, currency?: string): Amount | undefined {
    return !isNil(value) ? { value, currency } : undefined;
  }
}
